import React, { useContext, useCallback, useState, useMemo, useEffect } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { ImageSuggestionCarousel } from '@bingads-webui-campaign-react/image-suggestion-carousel';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Spinner } from '@fluentui/react-components';
import { Shimmer, ShimmerElementsGroup, ShimmerElementType, MessageBar, MessageBarType } from '@fluentui/react';
import { CroppedImageView } from '@bingads-webui-campaign-react/cropped-image-view';
import { useCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { overrideClasses } from './carousel-style';
import { getImageSuggestionCarouselStyles } from './styles';
import { VideoRecommendationsPanel } from './video-recommendations-panel';

const IMAGE_DIM = {
  width: 106,
  height: 76,
  marginRight: 8,
};

const initialImageContainerLeftMargin = 10;
const SuggestionViewImageCount = 5;
const MaxSuggestionsInCarousel = 15;

const LoadingShimmer = ({ i18n }) => {
  const shimmerElements = useMemo(() => {
    const shimmerElement = [];

    _.each(_.range(SuggestionViewImageCount), () => {
      shimmerElement.push({ type: ShimmerElementType.line, height: 76, width: 106 });
      shimmerElement.push({ type: ShimmerElementType.gap, width: 10, height: 76 });
    });

    return shimmerElement;
  }, []);

  return (
    <div style={{ display: 'flex', width: '410px', flexWrap: 'wrap' }}>
      <Spinner labelPosition="after" size="extra-tiny" label={i18n.getString(_TL_('Generating new asset recommendations...'))} />
      <Shimmer style={{ width: '100%' }} customElementsGroup={<ShimmerElementsGroup width="90px" shimmerElements={shimmerElements} />} />
    </div>
  );
};

LoadingShimmer.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
};

const getImageDimension = src => new Promise((resolve) => {
  const img = new Image();
  img.src = src;
  img.onload = () => {
    const width = img.naturalWidth;
    const height = img.naturalHeight;

    resolve({
      imgWidth: width, imgHeight: height,
    });
  };
});

const ImageView = ({ src }) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  const aspectRatio = width / height;
  const maxWidth = 107;

  const resizeHeight = Math.min(maxWidth / aspectRatio, 76);

  useEffect(() => {
    getImageDimension(src).then(({ imgWidth, imgHeight }) => {
      setWidth(imgWidth);
      setHeight(imgHeight);
    });
  }, [src]);

  if (!width || !height) {
    return <div />;
  }

  return (
    <CroppedImageView
      src={src}
      originalWidth={width}
      aspectRatio={aspectRatio}
      resizeImageHeight={resizeHeight}
      SourceWidth={width}
    />
  );
};

ImageView.propTypes = {
  src: PropTypes.string.isRequired,
};

export const VideoAdsSuggestionCarousel = ({
  onSelectVideoSuggestion,
  i18n,
  permissions,
  useBrandKit = false,
  isBrandKitEmpty,
  setUseBrandKit,
  multiVideoMode,
}) => {
  // For Video BK will only work with new templates
  const isVideoTemplateUIEnabled = permissions && permissions.dynamic && permissions.dynamic.VideoTemplateUI;
  const isBrandKitEnabled = permissions && permissions.dynamic && (permissions.dynamic.BrandKitUI && isVideoTemplateUIEnabled);
  const activity = useContext(ActivityContext);
  const [isSeeAllPanelOpen, setIsSeeAllPanelOpen] = useState(false);
  const [videosToSuggest, setVideosToSuggest] = useState([]);

  const [recommendationContext, , reportRecommendations] = useContext(RecommendationContext);
  const { logSuggestionsViewed } = useCopilotLoggers();

  const {
    videoAssets: videoSuggestions,
    isLoading,
    errorMessage,
  } = recommendationContext || {};

  const classes = useDefaultStyles(getImageSuggestionCarouselStyles);

  const { carouselState } = useMemo(() => ({
    carouselState: {
      carousel: {
        isError: _.isEmpty(videoSuggestions),
      },
      suggestion: {
        images: videoSuggestions,
      },
    },
    panelState: {
      panelImages: videoSuggestions,
      hasMore: false,
    },
  }), [videoSuggestions]);

  const { suggestion, carousel } = carouselState || {};
  const { images: suggestionImages } = suggestion || {};

  const onOffsetChange = useCallback((offset) => {
    const viewedImages = videosToSuggest.slice(offset, offset + SuggestionViewImageCount);
    const suggestionIds = _.pluck(viewedImages, 'suggestionId');
    reportRecommendations({ view: suggestionIds });
    logSuggestionsViewed(undefined /* imageSuggestionIds */, suggestionIds);
  }, [videosToSuggest, reportRecommendations, logSuggestionsViewed]);

  const onSelectIndex = useCallback((index) => {
    const suggestedVideo = videosToSuggest[index];
    // log the generated recommendations.
    if (activity && suggestedVideo) {
      const recommendationDetails = {
        isNativeAd: multiVideoMode,
        isVideoTemplateUIEnabled,
        url: suggestedVideo.url,
        recommendationId: suggestedVideo.RecommendationId,
        templateId: suggestedVideo.template && suggestedVideo.template.templateId,
      };
      activity.trace(`VideoAdsCopilot:VideoRecommendationsSelected, ${JSON.stringify(recommendationDetails)}`);
    }
    onSelectVideoSuggestion(suggestedVideo);
  }, [videosToSuggest, onSelectVideoSuggestion]);

  const onSelectImage = useCallback((__, index) => {
    onSelectIndex(index);
  }, [onSelectIndex]);

  const onSelectSeeAllImages = useCallback(() => {
    const suggestionIds = suggestionImages.map(({ suggestionId }) => suggestionId);

    setIsSeeAllPanelOpen(true);
    logSuggestionsViewed(undefined /* imageSuggestionIds */, suggestionIds);
  }, [logSuggestionsViewed, suggestionImages]);

  const onRenderImage = useCallback((src, classesProp) => (
    <div className={classNames(classesProp.image, classes.croppedImage)}>
      <ImageView src={src} />
    </div>
  ), [classes.croppedImage]);

  useEffect(() => {
    const truncatedImages = _.isArray(suggestionImages) && suggestionImages.slice(0, MaxSuggestionsInCarousel);
    setVideosToSuggest(truncatedImages || []);
  }, [suggestionImages]);

  return (
    <div className={classes.imageSuggestionCarouselRoot}>
      {isLoading ?
        <LoadingShimmer i18n={i18n} /> : (
          <ImageSuggestionCarousel
            title={_TL_('Recommended videos')}
            helpTopic="pop_BA_VideoAdsCopilot_Videos"
            isLoading={isLoading}
            images={videosToSuggest}
            i18n={i18n}
            isEmpty={carousel && carousel.isError}
            onSelectImage={onSelectImage}
            onSelectSeeAll={onSelectSeeAllImages}
            showViewAllInHeader
            enableBrandKit={isBrandKitEnabled && !isBrandKitEmpty}
            useBrandKit={useBrandKit}
            setUseBrandKit={setUseBrandKit}
            alwaysShowViewAll
            stylingOverrides={{
              classOverrides: overrideClasses,
              initialImageContainerLeftMargin,
              seeAllLinkText: _TL_('View all'),
            }}
            onOffsetChange={onOffsetChange}
            onRenderImage={onRenderImage}
            imageDimension={IMAGE_DIM}
          />)}
      {isSeeAllPanelOpen && (
        <VideoRecommendationsPanel
          i18n={i18n}
          isOpen={isSeeAllPanelOpen}
          close={() => setIsSeeAllPanelOpen(false)}
          onSelectVideoSuggestion={onSelectIndex}
          permissions={permissions}
        />)}
      {errorMessage && (
        <MessageBar messageBarType={MessageBarType.error}>
          {i18n.getString(errorMessage)}
        </MessageBar>)}
    </div>
  );
};

VideoAdsSuggestionCarousel.propTypes = {
  onSelectVideoSuggestion: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({}).isRequired,
  useBrandKit: PropTypes.bool,
  isBrandKitEmpty: PropTypes.bool,
  setUseBrandKit: PropTypes.func,
};
